import './CashinClosingModeEditor.less';

import clsx from 'clsx';
import React, { FC, forwardRef, MouseEventHandler, useRef } from 'react';
import i18n from '../../../common/i18n';

import AnimateHeight from 'react-animate-height';
import ArrowRightIcon from '../../../icons/ArrowRightIcon';
import CommonButton from '../../common-button/view/CommonButton';
import CheckButton from '../../forms/CheckButton';
import ArrowDownIcon from '../../../icons/svg/arrow-down-icon-sv.svg';
import { formatMoneyAmount } from '../../../common/util';
import SpeedingEuroIcon from '../../../icons/SpeedingEuroIcon';
import ModerateError from '../../errors/ModerateError/ModerateError';
import { useCashinButton } from './useCashinHooks';
import { CustomerChangeApproval } from '../constants';
import { useDarkTheme } from '../../../common/theme';

interface ClosingModeEditorProps {
    allPicksChosen: boolean;
    amount: number;
    currentEventualWin: number;
    checkboxValue: string;
    selectedDisabledCount: number;
    hideCheckboxes?: boolean;
    isCashinButtonDisabled?: boolean;
    minimalizedEditor?: boolean;
    newEventualWin: number;
    onCheckboxChange: (value: string) => void;
    onCancelButtonClick: () => void;
    onSubmitButtonClick: () => void;
    onCashinButtonClick: (value: React.MutableRefObject<any>) => MouseEventHandler;
    selectedCashinsCount: number;
    toggleHideCheckboxes?: () => void;
    isTransactionActive?: boolean;
    portal?: boolean;
    ticketId: string;
}

const getCashinsCountTranslation = count => {
    if (count === 0 || count > 4) return i18n('nui.ticketus.cashin.selected-cashin-count');
    if (count === 1) return i18n('nui.ticketus.cashin.selected-cashin-count-1');
    if (count > 1 && count < 5) return i18n('nui.ticketus.cashin.selected-cashin-count-2');
};

const getCashinDisabledCountTranslation = (selectedDisabledCount, totalCount) => {
    if (selectedDisabledCount === 1)
        return i18n(
            'nui.ticketus.cashin.selected-cashin-count-disabled',
            selectedDisabledCount.toString(),
            totalCount.toString()
        );
    if (selectedDisabledCount > 1 && selectedDisabledCount < 5)
        return i18n(
            'nui.ticketus.cashin.selected-cashin-count-disabled-1',
            selectedDisabledCount.toString(),
            totalCount.toString()
        );
    return i18n(
        'nui.ticketus.cashin.selected-cashin-count-disabled-2',
        selectedDisabledCount.toString(),
        totalCount.toString()
    );
};

const getSelectedCheckboxTitle = checkboxValue => {
    if (checkboxValue === CustomerChangeApproval.ONLY_HIGHER)
        return i18n('nui.ticketus.cashin.closing-mode.only-higher-win');
    if (checkboxValue === CustomerChangeApproval.ALL) return i18n('nui.ticketus.cashin.closing-mode.all-win-changes');
};

const cashinOutLabel = amount => (
    <>
        <SpeedingEuroIcon height={20} className="icon" fill="#fff" />
        {i18n('commons.term.odkup-mozny-alt')}&nbsp;{formatMoneyAmount(amount, `\u00A0€`)}
    </>
);
/**
 * Component uzatvaranie typo
 * @param allPicksChosen
 * @param amount
 * @param checkboxValue defaultna hodnota checkboxu
 * @param currentEventualWin aktualna eventualna vyhra
 * @param hideCheckboxes parameter na zobrazenie/skrytie checkboxov
 * @param isCashinButtonDisabled parameter disablovania buttonu Cashin
 * @param minimalizedEditor parameter minimalizovania casti s buttonmi
 * @param newEventualWin nova eventualna vyhra
 * @param onCheckboxChange funkcia na handlovanie checkboxov
 * @param onCancelButtonClick funkcia na handlovanie butonu zrusenia
 * @param onSubmitButtonClick   funkcia na hadlovanie buttonu potvrdenia
 * @param onCashinButtonClick
 * @param selectedCashinsCount pocet vybranych uzavretych tipov
 * @param selectedDisabledCount pocet vybranych uzavretych tipov ktore su nedostupne
 * @param toggleHideCheckboxes funkcia na zobrazenie/skrytie banneru s checkboxami
 */
const CashinClosingModeEditor: FC<ClosingModeEditorProps> = forwardRef<HTMLButtonElement, ClosingModeEditorProps>(
    (
        {
            allPicksChosen = false,
            amount = null,
            checkboxValue,
            currentEventualWin,
            hideCheckboxes,
            isCashinButtonDisabled = false,
            minimalizedEditor,
            newEventualWin,
            onCheckboxChange,
            onCancelButtonClick,
            onSubmitButtonClick,
            onCashinButtonClick,
            selectedCashinsCount = 0,
            selectedDisabledCount,
            toggleHideCheckboxes,
            isTransactionActive = false,
            portal = false,
            ticketId
        },
        ref: React.MutableRefObject<any>
    ) => {
        const cashinButtonRef = useRef(null);
        const isDarkTheme = useDarkTheme();
        const { hidden, disabled } = useCashinButton(ticketId);
        return (
            <div
                className={clsx('cashin-closing-mode-editor-wrapper flex flex-col py-10 px-20 p-rel', {
                    'cashin-dark': isDarkTheme,
                    'py-20': portal,
                    'cashin-light': !isDarkTheme
                })}
            >
                {minimalizedEditor ? (
                    <div className="flex-center closing-mode-arrow-up" data-atid="closing-mode-arrow-up">
                        <ArrowDownIcon />
                    </div>
                ) : null}
                {!isTransactionActive && allPicksChosen ? (
                    <ModerateError
                        text={`${i18n('nui.ticketus.cashin.closing-mode-editor.odkup-mozny-alt')} ${formatMoneyAmount(
                            amount,
                            `\u00A0€`
                        )}`}
                    />
                ) : (
                    <>
                        <div className="full-width flex items-center mb-5">
                            <div className="flex flex-col c-default-light fs-text flex-3">
                                {i18n('nui.ticketus.cashin.closing-mode-editor.current-win')}
                                <div className="fs-cash c-default-light mt-5" data-atid="current-eventual-win">
                                    {formatMoneyAmount(currentEventualWin, `\u00A0€`)}
                                </div>
                            </div>
                            <ArrowRightIcon classname="flex-1" />
                            <div className="flex flex-col c-default-light fs-text flex-3 items-end">
                                {i18n('nui.ticketus.cashin.closing-mode-editor.new-win')}
                                <div
                                    className="fs-21 text-extra-bold c-black-100 c-default-light mt-5"
                                    data-atid="new-eventual-win"
                                >
                                    {isCashinButtonDisabled || !newEventualWin
                                        ? '...'
                                        : formatMoneyAmount(newEventualWin, `\u00A0€`)}
                                </div>
                            </div>
                        </div>

                        <div className="full-width flex items-center">
                            <div className="cashin-count-border flex-1" />
                            <div
                                className={clsx('cashin-count px-15 fs-12 py-3 flex items-center justify-center', {
                                    selected: selectedCashinsCount > 0,
                                    disabledSelected: selectedDisabledCount > 0
                                })}
                                data-atid="selected-cashins-count"
                            >
                                {selectedDisabledCount === 0
                                    ? selectedCashinsCount + ' ' + getCashinsCountTranslation(selectedCashinsCount)
                                    : getCashinDisabledCountTranslation(
                                          selectedDisabledCount,
                                          selectedDisabledCount + selectedCashinsCount
                                      )}
                            </div>
                            <div className="cashin-count-border flex-1" />
                        </div>
                    </>
                )}
                <AnimateHeight height={minimalizedEditor ? 0 : 'auto'}>
                    {hideCheckboxes ? (
                        <div className="flex items-center justify-between mt-5">
                            <div className="fs-info">{getSelectedCheckboxTitle(checkboxValue)}</div>
                            <button
                                className="nui-blue-link br-none p-0 pointer fs-button text-bold"
                                onClick={toggleHideCheckboxes}
                                data-atid="hide-checkboxes"
                            >
                                {i18n('nui.ticketus.cashin.checkbox-change')}
                            </button>
                        </div>
                    ) : (
                        <div className={clsx('flex flex-col mt-5')}>
                            <CheckButton
                                active={checkboxValue === CustomerChangeApproval.ONLY_HIGHER}
                                onClick={() => onCheckboxChange(CustomerChangeApproval.ONLY_HIGHER)}
                                disabled={isTransactionActive}
                                data-atid="check-only-higher-win"
                            >
                                {i18n('nui.ticketus.cashin.closing-mode.only-higher-win')}
                            </CheckButton>
                            <CheckButton
                                active={checkboxValue === CustomerChangeApproval.ALL}
                                onClick={() => onCheckboxChange(CustomerChangeApproval.ALL)}
                                disabled={isTransactionActive}
                                data-atid="check-all-win-changes"
                            >
                                {i18n('nui.ticketus.cashin.closing-mode.all-win-changes')}
                            </CheckButton>
                        </div>
                    )}
                    <div className={clsx('full-width flex items-center mt-5', { 'mt-10': portal })}>
                        <CommonButton
                            className="nui-btn-secondary nui-btn-lg flex-1 fs-button"
                            color="grey"
                            label={i18n('nui.ticketus.cashin.closing-mode-cancel')}
                            disabled={isTransactionActive}
                            onClick={onCancelButtonClick}
                            dataAtid="on-cancel-button"
                        />
                        {allPicksChosen && !hidden ? (
                            <CommonButton
                                cRef={cashinButtonRef}
                                className="nui-btn-primary nui-btn-lg flex-2 ml-15 fs-button"
                                label={cashinOutLabel(amount)}
                                color="green"
                                onClick={onCashinButtonClick(cashinButtonRef)}
                                disabled={disabled}
                                dataAtid="cashin-button"
                            />
                        ) : (
                            <CommonButton
                                cRef={ref}
                                className="nui-btn-primary nui-btn-lg flex-2 ml-15 fs-button"
                                color="blue"
                                disabled={isCashinButtonDisabled || newEventualWin === 0.0 || isTransactionActive}
                                label={i18n('nui.ticketus.cashin.close-selected')}
                                onClick={onSubmitButtonClick}
                                dataAtid="submit-button"
                            />
                        )}
                    </div>
                </AnimateHeight>
            </div>
        );
    }
);

export default CashinClosingModeEditor;
